import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import type { IFormSelectGroupOption } from '@/components/common/form/types';

import { formatDate } from '@/helpers';
import { getWeeksArray } from '@/store/slices/utilsSlice';
import { SelectGroup } from '@/components/common';
import { ChevronDown } from '@/assets/icons';
import { media } from '@/styles/theme';
import { useAppDispatch, useAppSelector, usePrevious, useWeek } from '@/hooks';
import {
  MONTH_DAY_FORMAT,
  OFFSET_MINUS,
  OFFSET_PLUS,
  SET_NEXT_WEEK,
  SET_PREVIOUS_WEEK,
  SIDEBAR_DELAY,
} from '@/config/variables';
import { CalendarDisplayRange } from '@/store/enums';
import {
  selectDeviceType,
  selectIsMobileScreenSize,
  selectViewMode,
  selectWeeks,
} from '@/store/selectors/utilsSelectors';
import { LoaderWrapper } from './loaders';
import { WeeksRangePickerSkeleton } from './WeeksRangePicker.skeleton';

interface IProps {
  showDaySpanSelect?: boolean;
  dateFormat?: {
    start?: string;
    end?: string;
  };
  weeksHorizon?: number;
  className?: string;
}

const weeksRangePickerSelector = createSelector(
  selectWeeks,
  selectIsMobileScreenSize,
  selectDeviceType,
  selectViewMode,
  (weeksList, isMobileScreenSize, deviceType, viewMode) => ({ weeksList, isMobileScreenSize, deviceType, viewMode }),
);

const WeeksRangePicker = ({
  showDaySpanSelect = true,
  dateFormat = {},
  weeksHorizon,
  className,
}: IProps): React.ReactElement => {
  const { start: dateFormatStart = MONTH_DAY_FORMAT, end: dateFormatEnd = MONTH_DAY_FORMAT } = dateFormat;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { weeksList, deviceType, viewMode } = useAppSelector(weeksRangePickerSelector);
  const { selectedDays, selectedWeekId, selectDays, selectWeek, isCurrentWeek, currentWeek } = useWeek();
  const isLoading = Object.keys(weeksList).length === 0 || !selectedWeekId;

  const daySpanOptions: { value: CalendarDisplayRange; label: string }[] = useMemo(() => {
    const options = [
      { value: CalendarDisplayRange.THREE_DAYS, label: t`commonComponents.weeksRangePicker.selectOption2` },
      { value: CalendarDisplayRange.ONE_DAY, label: t`commonComponents.weeksRangePicker.selectOption3` },
    ];
    if (deviceType === 'desktop')
      options.unshift({ value: CalendarDisplayRange.WEEK, label: t`commonComponents.weeksRangePicker.selectOption1` });
    return options;
  }, [deviceType, t]);

  const transformWeeksList = useCallback((): IFormSelectGroupOption[] => {
    return Object.values(weeksList).map(({ weekId, startYear, endYear, startMonth, endMonth, startDay, endDay }) => ({
      value: weekId,
      label: isCurrentWeek
        ? t`commonComponents.weeksRangePicker.labelThisWeek`
        : `
          ${formatDate(new Date(startYear, startMonth - 1, startDay), dateFormatStart)}
          -
          ${formatDate(new Date(endYear, endMonth - 1, endDay), dateFormatEnd)}
        `,
    }));
  }, [weeksList, isCurrentWeek, t, dateFormatStart, dateFormatEnd]);

  const prevWeekId = usePrevious(selectedWeekId);

  useEffect(() => {
    if (!selectedWeekId) {
      return;
    }
    if (!Object.keys(weeksList).length) {
      dispatch(
        getWeeksArray({
          weekId: selectedWeekId,
          offsetMinus: OFFSET_MINUS,
          offsetPlus: OFFSET_PLUS,
        }),
      );
      return;
    }

    // load missing weeks if needed
    const isPrevButtonClicked = prevWeekId !== undefined && prevWeekId > selectedWeekId;
    const hasOffsetFromPast = weeksList[selectedWeekId - 1];
    const hasOffsetFromFuture = weeksList[selectedWeekId + 1];

    const loadMissingOffsets =
      (isPrevButtonClicked && !hasOffsetFromPast) || (!isPrevButtonClicked && !hasOffsetFromFuture);

    if (!selectedWeekId || !loadMissingOffsets) {
      return;
    }

    const promise = dispatch(
      getWeeksArray({
        weekId: selectedWeekId,
        offsetMinus: OFFSET_MINUS,
        offsetPlus: OFFSET_PLUS,
      }),
    );

    return () => {
      promise.abort();
    };
  }, [selectedWeekId]);

  useEffect(() => {
    if (deviceType === 'desktop') {
      selectDays(CalendarDisplayRange.WEEK);
    } else if (viewMode === 'portrait') {
      selectDays(CalendarDisplayRange.ONE_DAY);
    } else {
      selectDays(CalendarDisplayRange.THREE_DAYS);
    }
  }, [deviceType, selectDays, viewMode]);

  const handleChangeWeekId = (weekOffset: number) => {
    const newWeekId = selectedWeekId + weekOffset;

    if (weeksList[newWeekId]) {
      selectWeek(newWeekId);
    }
  };

  const handleChangeWeekRange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    selectDays(Number(event.target.value));
  };

  return (
    <div>
      <Row>
        <LoaderWrapper className="h-100" isLoading={isLoading} loader={<WeeksRangePickerSkeleton />}>
          <SelectGroup
            className={className}
            options={transformWeeksList()}
            defaultValue={selectedWeekId}
            onLeftClick={() => handleChangeWeekId(SET_PREVIOUS_WEEK)}
            onRightClick={() => handleChangeWeekId(SET_NEXT_WEEK)}
            blockOnRightClick={Boolean(weeksHorizon && selectedWeekId === currentWeek + weeksHorizon)}
          />
          {showDaySpanSelect && (
            <DaysSpanSelectInput onChange={handleChangeWeekRange} value={selectedDays}>
              {daySpanOptions.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </DaysSpanSelectInput>
          )}
        </LoaderWrapper>
      </Row>
    </div>
  );
};

export { WeeksRangePicker };

const Row = styled.div.attrs({
  className: `mt-1 mb-2 flex-wrap`,
})`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-wrap: wrap;

  ${media.tablet} {
    flex-direction: row;
    align-items: flex-start;
  }

  .input-group-prepend {
    margin-right: -1px !important;
  }
`;

const DaysSpanSelectInput = styled.select`
  height: 46px;
  width: 100%;
  padding-right: 23px;
  padding-left: 17px;

  background-color: #fff;
  background-image: url(${ChevronDown});
  background-position: right 12px center;
  background-repeat: no-repeat;
  border: 1px solid #ced4da;
  border-radius: 10px;

  font-size: 14px;
  color: #000000;
  text-align: center;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: width ${SIDEBAR_DELAY}ms ease-in-out, margin ${SIDEBAR_DELAY}ms ease-in-out,
    background-position ${SIDEBAR_DELAY}ms ease-in-out, padding ${SIDEBAR_DELAY}ms ease-in-out;

  .collapsed & {
    width: 38px;
    padding-left: 5px;
    padding-right: 18px;
    font-variant-numeric: tabular-nums;
    background-position: right 5px center;
  }

  ${media.tablet} {
    width: 97px;
    border-radius: 5px;
    text-align: left;
  }
`;
